import { useSalesForecast } from '../hooks/useSalesForecast.js'
import { formatMoney } from '../lib/format.js'

// 7-day revenue forecast card. Sits on the Insights screen and gives
// the manager a tomorrow + week-ahead view they can use for staffing.
//
// States:
//   loading       — small placeholder
//   no data       — friendly "import 2+ weeks of sales" prompt
//   ready         — bar chart-ish visual + per-day rows
//   error         — retry button
export default function SalesForecastCard() {
  const { forecast, totalForecast, avgConfidence, hasData, loading, error, refresh } = useSalesForecast()

  if (loading) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="text-[11px] font-medium text-black/50">
          7-day forecast
        </div>
        <div className="mt-2 text-sm text-black/50">Crunching the last 12 weeks…</div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-red-200 bg-red-50/40 p-4 shadow-sm">
        <div className="text-[11px] font-medium text-red-900/70">
          7-day forecast
        </div>
        <div className="mt-2 text-sm text-red-800">{error}</div>
        <button
          type="button"
          onClick={refresh}
          className="mt-2 rounded-md px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          Retry
        </button>
      </section>
    )
  }

  if (!hasData) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="text-[11px] font-medium text-black/55">
          7-day forecast
        </div>
        <div className="mt-2 text-sm text-black/65">
          Need at least 2 weeks of daily sales to start forecasting. Import a
          longer Toast or Square sales history (Settings → Item Sales) and the
          app will project the next 7 days using day-of-week patterns.
        </div>
      </section>
    )
  }

  // Find peak forecast for relative bar widths.
  const peak = Math.max(...forecast.map((r) => Number(r.forecast_revenue || 0)), 1)

  return (
    <section className="mx-4 mt-4 rounded-2xl border border-blue-200 bg-blue-50/30 p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-medium text-blue-900/70">
          ✨ 7-day forecast
        </div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
        >
          Refresh
        </button>
      </div>

      <div className="mt-2 flex items-baseline gap-3">
        <div>
          <div className="text-xs text-black/55">Next 7 days</div>
          <div className="text-2xl font-medium tabular-nums text-blue-900">
            {formatMoney(totalForecast)}
          </div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs text-black/55">Avg confidence</div>
          <div className="text-base font-medium tabular-nums text-blue-900">
            {avgConfidence}%
          </div>
        </div>
      </div>

      <ul className="mt-3 space-y-1.5">
        {forecast.map((row) => {
          const rev = Number(row.forecast_revenue || 0)
          const widthPct = Math.round((rev / peak) * 100)
          const conf = Number(row.confidence_pct || 0)
          const isToday = isSameDay(row.forecast_date)
          return (
            <li
              key={row.forecast_date}
              className={`grid grid-cols-[80px_1fr_80px] items-center gap-2 ${isToday ? 'font-medium' : ''}`}
            >
              <div className="text-xs text-black/65">
                {row.day_of_week} {isToday && <span className="text-blue-700">· today</span>}
              </div>
              <div className="relative h-5 rounded bg-blue-100/70 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded bg-gradient-to-r from-blue-500 to-blue-400"
                  style={{ width: `${Math.max(widthPct, 3)}%` }}
                />
              </div>
              <div className="text-right text-sm tabular-nums text-black/85">
                {formatMoney(rev)}
                <span className="ml-1 text-[10px] text-black/45">
                  · {conf}%
                </span>
              </div>
            </li>
          )
        })}
      </ul>

      <div className="mt-3 text-[11px] text-black/50 leading-snug">
        Average revenue per day-of-week from the last 12 weeks. Confidence reflects
        how consistent that DOW has been — low confidence means weeks vary a lot
        and the number is more of a best guess than a forecast.
      </div>
    </section>
  )
}

function isSameDay(forecastDateStr) {
  if (!forecastDateStr) return false
  const today = new Date().toLocaleDateString('en-CA') // YYYY-MM-DD in local tz
  return forecastDateStr === today
}
